/**
 * Kalender-Datum-Utilities für `AppointmentsPage.js` (06/2026 Refactor Phase 1).
 *
 * Pure Helpers — kein State, keine Hooks. Alle Funktionen sind seiteneffektfrei
 * und bekommen alle benötigten Eingaben als Argumente übergeben.
 */

/**
 * Liefert das Datum des Montags der Woche, in welcher `date` liegt.
 * Wochenstart in CH ist Montag (`day===0` → Sonntag, dann zurück auf vorheriges Montag).
 */
export const getWeekStart = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff));
};

/** Sieben Tage ab Montag der Woche von `currentDate`. */
export const getWeekDays = (currentDate) => {
  const weekStart = getWeekStart(currentDate);
  const days = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(weekStart);
    day.setDate(day.getDate() + i);
    days.push(day);
  }
  return days;
};

/**
 * Alle Tage die im Monats-Grid angezeigt werden — vom Montag der ersten
 * Monatswoche bis zum Sonntag der letzten Monatswoche.
 */
export const getDaysInMonth = (currentDate) => {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const days = [];

  const startDate = getWeekStart(firstDay);
  const endDate = new Date(lastDay);
  const endDay = endDate.getDay();
  endDate.setDate(endDate.getDate() + (endDay === 0 ? 0 : 7 - endDay));

  const current = new Date(startDate);
  while (current <= endDate) {
    days.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  return days;
};

/**
 * Formatiert das Datum für den Header je nach View ('day' | 'week' | 'month').
 * Reine Anzeige-Logik — kein Side-Effect.
 */
export const formatHeaderDate = (currentDate, view) => {
  if (view === 'week') {
    const weekStart = getWeekStart(currentDate);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    return `${weekStart.toLocaleDateString('de-DE', { day: 'numeric', month: 'short' })} - ${weekEnd.toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' })}`;
  }
  if (view === 'month') {
    return currentDate.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
  }
  return currentDate.toLocaleDateString('de-DE', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/**
 * Vorwärts/Rückwärts-Navigation. Liefert ein neues Datum — der Caller setzt
 * den State. Bei 'month' wird der Tag clamped (Leap-Year-safe).
 */
export const advanceDate = (currentDate, view, direction) => {
  const newDate = new Date(currentDate);
  if (view === 'day') {
    newDate.setDate(newDate.getDate() + direction);
  } else if (view === 'week') {
    newDate.setDate(newDate.getDate() + direction * 7);
  } else {
    const targetMonth = newDate.getMonth() + direction;
    const day = newDate.getDate();
    newDate.setDate(1);
    newDate.setMonth(targetMonth);
    const maxDay = new Date(newDate.getFullYear(), newDate.getMonth() + 1, 0).getDate();
    newDate.setDate(Math.min(day, maxDay));
  }
  return newDate;
};

/**
 * Prüft ob das Studio an einem Datum geöffnet ist.
 * `studioOpeningHours` ist das Settings-Objekt mit Wochentag-Keys
 * ('monday'..'sunday') und jeweils `{enabled|active: bool}`.
 */
export const isStudioOpenOnDate = (date, studioOpeningHours = {}) => {
  if (!date || !studioOpeningHours || Object.keys(studioOpeningHours).length === 0) {
    return true; // Default: offen
  }
  const dayMapping = [
    'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday',
  ];
  const dayKey = dayMapping[date.getDay()];
  const dayData = studioOpeningHours[dayKey];
  if (!dayData) return true;
  // Akzeptiere altes (`enabled`) und neues (`active`) Schema
  return dayData.enabled === true || dayData.active === true;
};
